import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, User, Mail, Phone, Globe, Briefcase, DollarSign, Tag, CheckCircle } from 'lucide-react';
import { registerWholesaleLead } from '../lib/api';
import { useApp } from '../context/AppContext';
import { Toaster, toast } from 'sonner';

const BUSINESS_TYPES = ['Distributor', 'Retailer', 'Exporter', 'Reseller', 'Other'];
const VOLUMES = ['< $5k', '$5k–$25k', '$25k–$100k', '$100k+'];
const CATEGORIES = [
  { value: 'NBK', label: { en: 'NBK (Laptops)', es: 'NBK (Portátiles)' } },
  { value: 'PC', label: { en: 'PC (Desktops)', es: 'PC (Escritorio)' } },
  { value: 'Apple', label: { en: 'Apple', es: 'Apple' } },
  { value: 'AIO', label: { en: 'AIO (All-in-One)', es: 'AIO (Todo en Uno)' } },
  { value: 'LCD', label: { en: 'LCD (Monitors)', es: 'LCD (Monitores)' } },
  { value: 'Workstations', label: { en: 'Workstations', es: 'Estaciones de Trabajo' } },
];

const T = {
  en: {
    headline: 'Register for Wholesale Access',
    subtext: 'Get weekly inventory lists, promos, and new arrivals for Latin America wholesale buyers.',
    companyName: 'Company Name',
    contactName: 'Contact Name',
    email: 'Email',
    country: 'Country',
    businessType: 'Business Type',
    selectType: 'Select type...',
    monthlyVolume: 'Monthly Purchase Volume',
    selectVolume: 'Select range...',
    categories: 'Interested Categories',
    optIn: 'Yes, email me weekly inventory lists + promos.',
    unsubscribe: 'You can unsubscribe anytime.',
    submit: 'Register',
    submitting: 'Submitting...',
    required: 'Required',
    successTitle: 'Registration received!',
    successMsg: 'Thanks — we received your registration. Our team will review and contact you shortly.',
    successBack: 'Back to Home',
  },
  es: {
    headline: 'Regístrese para Acceso Mayorista',
    subtext: 'Reciba listas de inventario semanales, promociones y novedades para compradores mayoristas de América Latina.',
    companyName: 'Nombre de la Empresa',
    contactName: 'Nombre de Contacto',
    email: 'Correo Electrónico',
    country: 'País',
    businessType: 'Tipo de Negocio',
    selectType: 'Seleccionar tipo...',
    monthlyVolume: 'Volumen Mensual de Compra',
    selectVolume: 'Seleccionar rango...',
    categories: 'Categorías de Interés',
    optIn: 'Sí, envíenme listas de inventario semanales + promociones.',
    unsubscribe: 'Puedes darte de baja en cualquier momento.',
    submit: 'Registrarse',
    submitting: 'Enviando...',
    required: 'Requerido',
    successTitle: '¡Registro recibido!',
    successMsg: 'Gracias — recibimos su registro. Nuestro equipo lo revisará y se pondrá en contacto pronto.',
    successBack: 'Volver al Inicio',
  },
};

export default function WholesaleRegisterPage() {
  const { language } = useApp();
  const navigate = useNavigate();
  const t = T[language] || T.en;
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    country: '',
    business_type: '',
    monthly_volume: '',
    interested_categories: [],
    marketing_opt_in: false,
  });

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const toggleCategory = (cat) => {
    setForm(prev => ({
      ...prev,
      interested_categories: prev.interested_categories.includes(cat)
        ? prev.interested_categories.filter(c => c !== cat)
        : [...prev.interested_categories, cat],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_name || !form.email || !form.country || !form.business_type) {
      toast.error(language === 'es' ? 'Complete todos los campos requeridos' : 'Please fill all required fields');
      return;
    }
    setLoading(true);
    try {
      await registerWholesaleLead(form);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" data-testid="register-success">
        <div className="max-w-lg w-full text-center py-16">
          <CheckCircle className="w-16 h-16 text-[#10B981] mx-auto mb-6" />
          <h1 className="text-3xl font-bold mb-4">{t.successTitle}</h1>
          <p className="text-white/60 text-lg mb-8">{t.successMsg}</p>
          <button
            onClick={() => navigate('/')}
            className="btn-primary"
            data-testid="back-home-btn"
          >
            {t.successBack}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-16 px-4" data-testid="wholesale-register-page">
      <Toaster position="top-right" theme="dark" />
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">{t.headline}</h1>
          <p className="text-white/50 text-lg max-w-xl mx-auto">{t.subtext}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="card-dark p-8 space-y-6">
          {/* Company + Contact */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-white/60 mb-2 flex items-center gap-2">
                <Building2 className="w-4 h-4" /> {t.companyName} <span className="text-[#EF4444]">*</span>
              </label>
              <input
                type="text"
                value={form.company_name}
                onChange={(e) => updateField('company_name', e.target.value)}
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-[#00D4FF]"
                required
                data-testid="input-company"
              />
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-2 flex items-center gap-2">
                <User className="w-4 h-4" /> {t.contactName} <span className="text-[#EF4444]">*</span>
              </label>
              <input
                type="text"
                value={form.contact_name}
                onChange={(e) => updateField('contact_name', e.target.value)}
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-[#00D4FF]"
                required
                data-testid="input-contact"
              />
            </div>
          </div>

          {/* Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-white/60 mb-2 flex items-center gap-2">
                <Mail className="w-4 h-4" /> {t.email} <span className="text-[#EF4444]">*</span>
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => updateField('email', e.target.value)}
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-[#00D4FF]"
                required
                data-testid="input-email"
              />
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-2 flex items-center gap-2">
                <Phone className="w-4 h-4" /> {language === 'es' ? 'Teléfono' : 'Phone'}
              </label>
              <input
                type="tel"
                value={form.phone || ''}
                onChange={(e) => updateField('phone', e.target.value)}
                placeholder="+1 (555) 000-0000"
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-[#00D4FF]"
                data-testid="input-phone"
              />
            </div>
          </div>

          {/* Country + Business Type */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-white/60 mb-2 flex items-center gap-2">
                <Globe className="w-4 h-4" /> {t.country} <span className="text-[#EF4444]">*</span>
              </label>
              <input
                type="text"
                value={form.country}
                onChange={(e) => updateField('country', e.target.value)}
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-[#00D4FF]"
                required
                data-testid="input-country"
              />
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-2 flex items-center gap-2">
                <Briefcase className="w-4 h-4" /> {t.businessType} <span className="text-[#EF4444]">*</span>
              </label>
              <select
                value={form.business_type}
                onChange={(e) => updateField('business_type', e.target.value)}
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-[#00D4FF]"
                required
                data-testid="select-business-type"
              >
                <option value="">{t.selectType}</option>
                {BUSINESS_TYPES.map(bt => <option key={bt} value={bt}>{bt}</option>)}
              </select>
            </div>
          </div>

          {/* Monthly Volume */}
          <div>
            <label className="block text-sm text-white/60 mb-2 flex items-center gap-2">
              <DollarSign className="w-4 h-4" /> {t.monthlyVolume}
            </label>
            <select
              value={form.monthly_volume}
              onChange={(e) => updateField('monthly_volume', e.target.value)}
              className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-[#00D4FF]"
              data-testid="select-volume"
            >
              <option value="">{t.selectVolume}</option>
              {VOLUMES.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>

          {/* Categories Multi-Select */}
          <div>
            <label className="block text-sm text-white/60 mb-3 flex items-center gap-2">
              <Tag className="w-4 h-4" /> {t.categories}
            </label>
            <div className="flex flex-wrap gap-2" data-testid="category-chips">
              {CATEGORIES.map(cat => {
                const isSelected = form.interested_categories.includes(cat.value);
                return (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => toggleCategory(cat.value)}
                    className={`px-4 py-2 text-sm border transition-colors ${
                      isSelected
                        ? 'border-[#00D4FF] bg-[#00D4FF]/10 text-[#00D4FF]'
                        : 'border-white/10 text-white/50 hover:border-white/20'
                    }`}
                    data-testid={`cat-${cat.value}`}
                  >
                    {cat.label[language] || cat.label.en}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Marketing Opt-In */}
          <div className="p-4 bg-[#050505] border border-white/10">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="opt-in-label">
              <input
                type="checkbox"
                checked={form.marketing_opt_in}
                onChange={(e) => updateField('marketing_opt_in', e.target.checked)}
                className="mt-1 w-5 h-5 accent-[#00D4FF] shrink-0"
                data-testid="opt-in-checkbox"
              />
              <div>
                <span className="text-sm text-white">{t.optIn}</span>
                <p className="text-xs text-white/30 mt-1">{t.unsubscribe}</p>
              </div>
            </label>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full !py-4 text-lg disabled:opacity-40"
            data-testid="submit-btn"
          >
            {loading ? t.submitting : t.submit}
          </button>
        </form>
      </div>
    </div>
  );
}
